import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { OnboardingChat, type AthleteOnboardingProfile } from '@/components/onboarding/OnboardingChat';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/hooks/use-toast';
import { CARD_NEBULA } from '@/styles/scheduleTheme';
import { log } from '@/lib/logger';

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, loading, status, refreshUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasRedirectedRef = useRef(false);

  // CRITICAL: Onboarding is ONLY for authenticated users with incomplete onboarding
  // If not authenticated, redirect to login (NOT a fallback for auth failures)
  // If onboarding already complete (backend is source of truth), redirect to dashboard
  useEffect(() => {
    // Block routing until auth resolves
    if (status === "bootstrapping" || loading) {
      return;
    }

    // CRITICAL: If unauthenticated, go to login (NOT onboarding)
    // Onboarding is a post-auth state, not an auth fallback
    if (status === "unauthenticated" || !user) {
      log.debug("ONBOARDING", "User not authenticated, redirecting to login");
      navigate('/login', { replace: true });
      return;
    }

    // CRITICAL: Backend is source of truth for onboarding completion
    // If backend says onboarding is complete, redirect to today
    if (status === "authenticated" && user && user.onboarding_complete && !hasRedirectedRef.current) {
      log.debug("ONBOARDING", "Backend confirms onboarding complete, redirecting to today");
      hasRedirectedRef.current = true;
      navigate('/today', { replace: true });
    }
  }, [user, loading, status, navigate]);

  const handleComplete = async (profile: AthleteOnboardingProfile): Promise<void> => {
    // CRITICAL: Frontend never decides onboarding completion
    // This callback is only called after backend confirms success via completeOnboarding()
    // Refresh user state from backend to get updated onboarding_complete value
    setIsSubmitting(true);
    
    try {
      log.debug("ONBOARDING", "Onboarding completed, refreshing user state from backend", { profile });
      await refreshUser();
      
      // Reset redirect ref to allow useEffect to handle redirect
      hasRedirectedRef.current = false;
      
      // Small delay to allow React state to update after refreshUser
      // The useEffect will check user.onboarding_complete and redirect if true
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Note: After refreshUser completes, the user state should be updated
      // The useEffect hook depends on `user`, so when user updates, it will check
      // user.onboarding_complete and redirect if true
      log.debug("ONBOARDING", "User state refreshed, waiting for useEffect to redirect based on backend state");
    } catch (error) {
      log.error("ONBOARDING", "Failed to refresh user state:", error);
      toast({
        title: 'Error',
        description: 'Failed to refresh user state. Please refresh the page to verify onboarding completion.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-[100dvh] flex flex-col relative overflow-hidden">
      {/* Starfield / Dark Nebula background — visible but non-interfering */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url('/stars.svg'), ${CARD_NEBULA}`,
          backgroundSize: 'cover, cover',
          backgroundRepeat: 'no-repeat, no-repeat',
          backgroundPosition: 'center, 70% 50%',
          opacity: 0.3,
        }}
      />

      {/* Fixed HUD System Status Header — does NOT scroll */}
      <header className="relative z-20 shrink-0 h-12 border-b border-[#00FFC8]/10 flex items-center justify-between px-5 backdrop-blur-[12px] bg-[rgba(11,14,20,0.85)]">
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#00FFC8]/80">
          AI Training Coach
        </span>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-muted-foreground">SYS:</span>
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#00FFC8] shadow-[0_0_6px_rgba(0,255,200,0.6)]" />
          <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-[#00FFC8]/70">ACTIVE</span>
        </div>
      </header>

      {/* Chat area — fills remaining space, scrolls internally */}
      <main className="relative z-10 flex-1 flex flex-col max-w-2xl mx-auto w-full min-h-0">
        <OnboardingChat onComplete={handleComplete} isComplete={isSubmitting} />
      </main>
    </div>
  );
}
